import { Injectable } from '@angular/core';
import axios from 'axios';
import { environment } from 'src/environments/environment';

export type FeePayeeCaseHistoryItem = {
  id: number;
  date: string;
  label: string;
  amount: string;
};

export type FeePayeeCase = {
  uuid: string;
  fee_id: number;
  remaining_amount: number;
  original_amount: number;
  paid_amount: number;
  event_net_paid_amount: number;
  currency_iso: string;
  state: 'pending' | 'paid' | 'refunded';
  fee_label: string;
  fee_type: 'payee' | 'indirect';
  payment_occurrence: 'start' | 'closed-success' | 'ad-hoc';
  history: FeePayeeCaseHistoryItem[];
};

export type AddableFee = {
  fee_id: number;
  label: string;
  amount: number;
  currency_iso: string;
  type: 'amount' | 'percentage' | 'unknown';
  fee_type: 'payee' | 'indirect';
  payment_occurrence: 'start' | 'closed-success' | 'ad-hoc';
};

export type GetFeePayeeCasesResponse = {
  fees: FeePayeeCase[];
  addable_fees: AddableFee[];
};

export type UpdateFeePayeeCaseParams = {
  original_amount: number;
  paid_amount: number;
  overwrite?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class FeePayeeCaseService {
  async getFees(caseId: string): Promise<GetFeePayeeCasesResponse> {
    const url = `${environment.baseUrl}/api/fee-payee-cases/${caseId}`;
    const result = await axios.get<GetFeePayeeCasesResponse>(url);
    return result.data;
  }

  async addFee(caseId: string, feeId: number): Promise<FeePayeeCase> {
    const url = `${environment.baseUrl}/api/fee-payee-cases/${caseId}`;
    const result = await axios.post<{ fee: FeePayeeCase }>(url, {
      fee_id: feeId,
    });
    return result.data.fee;
  }

  async updateFee(
    caseId: string,
    feeUuid: string,
    params: UpdateFeePayeeCaseParams,
  ): Promise<FeePayeeCase> {
    const url = `${environment.baseUrl}/api/fee-payee-cases/${caseId}/${feeUuid}`;
    const result = await axios.put<{ fee: FeePayeeCase }>(url, params);
    return result.data.fee;
  }

  async deleteFee(caseId: string, feeUuid: string): Promise<void> {
    const url = `${environment.baseUrl}/api/fee-payee-cases/${caseId}/${feeUuid}`;
    await axios.delete(url);
  }

  async refundFee(
    caseId: string,
    feeUuid: string,
    amount: number,
  ): Promise<FeePayeeCase> {
    const url = `${environment.baseUrl}/api/fee-payee-cases/${caseId}/${feeUuid}/refund`;
    const result = await axios.post<{ fee: FeePayeeCase }>(url, { amount });
    return result.data.fee;
  }
}
